import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Phone, Home, Calendar, KeyRound, Star, MapPin, Flame, CheckCircle, Bed, Bath } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import SEOHead from "@/components/seo/SEOHead";
import EqualHousingLogo from "@/components/compliance/EqualHousingLogo";
import { supabase } from "@/integrations/supabase/client";
import { trackGA4Event } from "@/lib/ga4";
import { trackAdsConversion } from "@/lib/googleAds";
import { useIsMobile } from "@/hooks/use-mobile";

import heroImage from "@/assets/hero-south-jersey-home.jpg";

const PHONE = "(856) 805-2279";
const PHONE_TEL = "tel:+18568052279";
const GOOGLE_ADS_ID = "AW-11071702370";


interface IDXListing {
  listingID?: string;
  address?: string;
  cityName?: string;
  state?: string;
  bedrooms?: string;
  fullBaths?: string;
  partialBaths?: string;
  sqFt?: string;
  fullDetailsURL?: string;
  idxStatus?: string;
  priceData?: { value?: { formatted?: string; raw?: number } };
  image?: { [key: string]: { url?: string } | number; totalCount?: number };
}

const TESTIMONIALS = [
  {
    text: "We couldn't be more grateful to have had Timothy as our realtor. Over the course of a year, he stood by us through every high and low of selling our house and finding our dream home — never once pressuring us, always supporting us, and going above and beyond in every way. His knowledge of the market, sharp negotiation skills, and incredible attention to detail made both the selling and buying processes smooth and successful. Thanks to his guidance, we sold our house confidently and are now living in a home that checks every box we dreamed of. If you're looking for someone who treats you like family while working like a pro — Timothy is your person. 🏡💫",
    name: "Brianna",
    town: "Williamstown",
  },
  {
    text: "I have had the pleasure of working with Prime on several deals. Everyone is very responsive and very easy to work with. I would highly recommend if you're looking to purchase or sell a home.",
    name: "Frank",
    town: "Berlin",
  },
  {
    text: "Everyone in this office was top notch to work with. We are already on our adventure to find our investment property after purchasing our new home.",
    name: "Theo",
    town: "Willingboro",
  },
  {
    text: "Thank you to the folks here for making the process seamless. Seriously, we couldn't have done this without you.",
    name: "Beatrice",
    town: "Cherry Hill",
  },
];

export interface TownLandingConfig {
  townName: string;
  slug: string;
  savedLinkId: string;
  viewAllUrl: string;
}

const TownBuyerLandingPage = ({ townName, slug, savedLinkId, viewAllUrl }: TownLandingConfig) => {
  const isMobile = useIsMobile();
  const formRef = useRef<HTMLDivElement>(null);
  const [formPastView, setFormPastView] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [timeline, setTimeline] = useState("");

  const location = townName;

  const [listings, setListings] = useState<IDXListing[]>([]);
  const [listingsLoading, setListingsLoading] = useState(true);
  const [totalCount, setTotalCount] = useState<number | null>(null);

  const [agentPhotoUrl, setAgentPhotoUrl] = useState<string | null>(null);

  useEffect(() => {
    if (document.getElementById("gads-gtag")) return;
    const script = document.createElement("script");
    script.id = "gads-gtag";
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ADS_ID}`;
    document.head.appendChild(script);
    window.dataLayer = window.dataLayer || [];
    const gtag = (...args: unknown[]) => { (window.dataLayer as unknown[]).push(args); };
    gtag("js", new Date());
    gtag("config", GOOGLE_ADS_ID);
  }, []);

  useEffect(() => {
    const fetchAgent = async () => {
      const { data } = await supabase
        .from("agents_public")
        .select("photo_url")
        .ilike("slug", "%timothy%")
        .maybeSingle();
      if (data?.photo_url) setAgentPhotoUrl(data.photo_url);
    };
    fetchAgent();
  }, []);

  useEffect(() => {
    const fetchListings = async () => {
      try {
        const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
        const anonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
        const res = await fetch(
          `https://${projectId}.supabase.co/functions/v1/idx-savedlink-results?savedLinkId=${savedLinkId}`,
          {
            headers: {
              apikey: anonKey,
              Authorization: `Bearer ${anonKey}`,
            },
          }
        );
        if (res.ok) {
          const json = await res.json();
          const arr: IDXListing[] = [];
          if (json && typeof json === "object") {
            for (const key of Object.keys(json)) {
              if (/^\d+$/.test(key) && typeof json[key] === "object") {
                arr.push(json[key]);
              }
            }
          }
          setTotalCount(arr.length);
          const comingSoon = arr.filter(l => (l.idxStatus || "").toLowerCase().includes("coming") || (l as any).propStatus?.toUpperCase()?.includes("COMING"));
          const others = arr.filter(l => !comingSoon.includes(l));
          const shuffle = <T,>(a: T[]): T[] => {
            for (let i = a.length - 1; i > 0; i--) {
              const j = Math.floor(Math.random() * (i + 1));
              [a[i], a[j]] = [a[j], a[i]];
            }
            return a;
          };
          const prioritized = [...shuffle(comingSoon), ...shuffle(others)];
          setListings(prioritized.slice(0, 3));
        }
      } catch (err) {
        console.error("Failed to fetch IDX listings for landing page:", err);
      } finally {
        setListingsLoading(false);
      }
    };
    fetchListings();
  }, [savedLinkId]);

  useEffect(() => {
    if (!isMobile) return;
    const observer = new IntersectionObserver(
      ([entry]) => setFormPastView(!entry.isIntersecting),
      { threshold: 0 }
    );
    const el = formRef.current;
    if (el) observer.observe(el);
    return () => { if (el) observer.unobserve(el); };
  }, [isMobile]);

  const [redirectUrl, setRedirectUrl] = useState(viewAllUrl);

  useEffect(() => {
    if (submitted) {
      const timer = setTimeout(() => {
        window.location.href = redirectUrl;
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [submitted, redirectUrl]);

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const handlePhoneClick = () => {
    trackGA4Event("phone_click", { category: "lead_action", label: `ppc_buyer_landing_${slug}` });
  };

  const fireGadsConversion = () => {
    const g = (window as unknown as { gtag?: (...args: unknown[]) => void }).gtag;
    if (typeof g === "function") {
      g("event", "conversion", {
        send_to: "AW-11071702370/NCc0CKDb1cIbEOKKs58p",
        value: 1.0,
        currency: "USD",
      });
    }
  };

  const getImageUrl = (listing: IDXListing): string | null => {
    if (listing.image && listing.image["0"] && typeof listing.image["0"] === "object") {
      return (listing.image["0"] as { url?: string }).url || null;
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const cleanPhone = phone.trim().replace(/^[^\d+]+/, "").replace(/[^\d+\s\-().]/g, "");
    if (!firstName.trim() || !email.trim() || !cleanPhone || !timeline) return;
    setSubmitting(true);

    try {
      const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;

      // 1. Sync to FUB (fire & forget)
      fetch(
        `https://${projectId}.supabase.co/functions/v1/submit-lead`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            event_type: "registration",
            lead_data: {
              name: `${firstName.trim()} Unknown`,
              email: email.trim(),
              phone: cleanPhone,
              message: `Looking in: ${location} | Timeline: ${timeline}`,
              tcpa_consent: true,
              consent_timestamp: new Date().toISOString(),
            },
            metadata: {
              source: `PPC Buyer Landing - ${townName}`,
              tags: [
                "PPC Buyer Lead",
                `Town: ${townName}`,
              ],
              location_interest: location,
              preferred_areas: location,
              timeframe: timeline,
              town: townName,
              page_url: window.location.href,
            },
          }),
        }
      ).catch(() => {});

      // 2. Create IDX lead + get authenticated login URL
      try {
        const idxRes = await fetch(
          `https://${projectId}.supabase.co/functions/v1/create-idx-lead`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              firstName: firstName.trim(),
              lastName: "Unknown",
              email: email.trim(),
              phone: cleanPhone,
              savedLinkUrl: viewAllUrl,
            }),
          }
        );
        if (idxRes.ok) {
          const idxJson = await idxRes.json();
          if (idxJson.loginUrl) {
            setRedirectUrl(idxJson.loginUrl);
          }
        }
      } catch {
        // Fallback: redirectUrl stays as viewAllUrl
      }

      // 3. DB insert
      const params = new URLSearchParams(window.location.search);
      await supabase.from("landing_page_leads").insert({
        landing_page_id: "00000000-0000-0000-0000-000000000001",
        name: `${firstName.trim()} Unknown`,
        email: email.trim(),
        phone: cleanPhone,
        message: `Looking in: ${location} | Timeline: ${timeline}`,
        tcpa_consent: true,
        consent_timestamp: new Date().toISOString(),
        utm_source: params.get("utm_source"),
        utm_medium: params.get("utm_medium"),
        utm_campaign: params.get("utm_campaign"),
        utm_content: params.get("utm_content"),
        utm_term: params.get("utm_term"),
      });

      // 4. GA4 + Google Ads
      trackGA4Event("form_submit", { category: "lead_action", form_name: `ppc_buyer_landing_${slug}`, location_interest: location });
      trackAdsConversion();
      fireGadsConversion();
      window.history.pushState({}, '', '?submitted=true');
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  const leadForm = (
    <div ref={formRef}>
      {/* Social proof trust bar */}
      {!submitted && (
        <div className="mb-4 text-center lg:text-left">
          <div className="flex items-center justify-center lg:justify-start gap-2">
            <div className="flex">
              {[1, 2, 3, 4, 5].map((s) => (
                <Star key={s} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
              ))}
            </div>
            <span className="text-sm font-medium text-white">4.9 on Google · 200+ Reviews</span>
          </div>
          <p className="text-xs text-white/85 mt-1">
            <span className="font-semibold">1,000+</span> Homes Sold in South Jersey
          </p>
        </div>
      )}
      <div className="bg-white rounded-lg shadow-xl p-6 md:p-8 w-full max-w-md mx-auto lg:mx-0">
      {submitted ? (
        <div className="text-center py-8">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-foreground mb-2">You're In!</h3>
          <p className="text-muted-foreground">Redirecting you to listings...</p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <h3 className="text-lg font-bold text-foreground text-center">{totalCount && totalCount > 0 ? `Get Instant Access to All ${totalCount} ${townName} Listings` : "Get Instant Access to This Week's Listings"}</h3>
          <p className="text-[13px] font-medium text-[#C8102E] text-center -mt-2">
            🏠 New listings added daily — don't miss out
          </p>
          <div>
            <Label htmlFor="firstName" className="sr-only">First Name</Label>
            <Input id="firstName" placeholder="First name" value={firstName} onChange={(e) => setFirstName(e.target.value)} required maxLength={100} className="h-12" />
          </div>
          <div>
            <Label htmlFor="email" className="sr-only">Email</Label>
            <Input id="email" type="email" placeholder="Your email" value={email} onChange={(e) => setEmail(e.target.value)} required maxLength={255} className="h-12" />
          </div>
          <div>
            <Label htmlFor="phone" className="sr-only">Phone</Label>
            <Input id="phone" type="tel" placeholder="Best phone number" value={phone} onChange={(e) => setPhone(e.target.value)} required maxLength={30} className="h-12" />
          </div>
          <div>
            <Label htmlFor="timeline" className="sr-only">When are you looking to move?</Label>
            <Select value={timeline} onValueChange={setTimeline} required>
              <SelectTrigger className="h-12">
                <SelectValue placeholder="When are you looking to move?" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Within 30 days">Within 30 days</SelectItem>
                <SelectItem value="1–3 months">1–3 months</SelectItem>
                <SelectItem value="3–6 months">3–6 months</SelectItem>
                <SelectItem value="Just browsing">Just browsing</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button
            type="submit"
            disabled={submitting}
            className="w-full h-12 text-base font-bold bg-[#C8102E] hover:bg-[#A00D24] text-white"
          >
            {submitting ? "Submitting..." : "SEE AVAILABLE HOMES →"}
          </Button>
          <p className="text-xs text-muted-foreground text-center">No spam. No obligation. A local agent will reach out within 1 hour.</p>
          <p className="text-[10px] text-muted-foreground/70 text-center leading-tight">
            By submitting this form, you agree to be contacted by MarChris Ventures LLC dba Prime Realty Partners via call, email, and text message regarding real estate and mortgage services at the number provided, including through automated means. Msg &amp; data rates may apply. This consent is not required as a condition of purchase. Reply STOP to opt out.
          </p>
        </form>
      )}
      </div>
    </div>
  );

  return (
    <>
      <SEOHead
        title={`Homes for Sale in ${townName} | See Listings This Week`}
        description={`Browse active homes for sale in ${townName}, NJ. Schedule a showing in 60 seconds with Prime Realty Partners — your local South Jersey real estate experts.`}
        canonicalUrl={`https://www.prpsj.com/${slug}`}
        noindex
        schema={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: `Homes for Sale in ${townName}`,
          description: `Browse active homes for sale in ${townName} and schedule a showing.`,
          url: `https://www.prpsj.com/${slug}`,
          publisher: {
            "@type": "RealEstateBrokerage",
            name: "Prime Realty Partners",
            url: "https://www.prpsj.com",
          },
        }}
      />

      {/* STICKY TOP BAR */}
      <div className="sticky top-0 z-50 bg-[#C8102E] text-white">
        <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between">
          <a href={PHONE_TEL} onClick={handlePhoneClick} className="flex items-center gap-2 font-semibold text-sm hover:underline">
            <Phone className="w-4 h-4" /> {PHONE}
          </a>
          <span className="text-xs md:text-sm font-medium bg-white/20 px-3 py-1 rounded-full">
            🏡 Homes Available This Week
          </span>
        </div>
      </div>

      {/* SECTION 1 — HERO */}
      <section className="relative min-h-[600px] md:min-h-[700px] flex items-center">
        <div className="absolute inset-0">
          <img src={heroImage} alt={`Beautiful ${townName} home`} width={1920} height={1080} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/50" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 py-12 md:py-20 w-full">
          <div className="flex flex-col lg:flex-row lg:items-center lg:gap-12">
            <div className="flex-1 mb-8 lg:mb-0">
              <h1 className="text-3xl md:text-5xl font-bold text-white leading-tight mb-4">
                {totalCount && totalCount > 0 ? `See the ${totalCount} Homes for Sale in ${townName} This Week` : `See Available Homes in ${townName} This Week`}
              </h1>
              <p className="text-lg md:text-xl text-white/90 max-w-lg">
                Browse active listings in {townName}, NJ — then schedule your showing in 60 seconds.
              </p>
            </div>
            <div className="flex-shrink-0 lg:w-[400px]">
              {leadForm}
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 2 — SOCIAL PROOF BAR */}
      <section className="bg-muted py-6">
        <div className="max-w-5xl mx-auto px-4 flex flex-col md:flex-row items-center justify-around gap-4 text-center">
          <div className="flex items-center gap-2">
            <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
            <span className="font-semibold text-foreground">4.6/5 Star Rating on Google</span>
          </div>
          <div className="flex items-center gap-2">
            <Home className="w-5 h-5 text-[#C8102E]" />
            <span className="font-semibold text-foreground">1,000+ South Jersey Homes Sold</span>
          </div>
          <div className="flex items-center gap-2">
            <MapPin className="w-5 h-5 text-[#C8102E]" />
            <span className="font-semibold text-foreground">Local Experts Since 2019</span>
          </div>
        </div>
      </section>

      {/* SECTION 3 — FEATURED LISTINGS */}
      <section className="py-16 bg-background">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-2">{totalCount && totalCount > 0 ? `${totalCount} Homes Available to Tour in ${townName}` : "Homes Available to Tour This Week"}</h2>
            <p className="text-muted-foreground">Updated daily from Bright MLS — these won't last long.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            {listingsLoading ? (
              Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="bg-card rounded-lg shadow-md overflow-hidden border">
                  <Skeleton className="h-48 w-full" />
                  <div className="p-4 space-y-3">
                    <Skeleton className="h-6 w-32" />
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-11 w-full" />
                  </div>
                </div>
              ))
            ) : listings.length > 0 ? (
              listings.map((listing, i) => {
                const imageUrl = getImageUrl(listing);
                const partialBaths = parseInt(listing.partialBaths || "0", 10);
                return (
                  <div key={listing.listingID || i} className="bg-card rounded-lg shadow-md overflow-hidden border">
                    <div className="relative">
                      {imageUrl ? (
                        <img src={imageUrl} alt={`${listing.address}, ${listing.cityName}`} loading="lazy" className="w-full h-48 object-cover" />
                      ) : (
                        <div className="w-full h-48 bg-muted flex items-center justify-center text-muted-foreground text-sm">No Photo</div>
                      )}
                      {listing.idxStatus && (
                        <span className="absolute top-3 left-3 bg-green-600 text-white text-xs font-bold px-2 py-1 rounded">
                          {listing.idxStatus.toUpperCase()}
                        </span>
                      )}
                    </div>
                    <div className="p-4">
                      <p className="text-xl font-bold text-[#C8102E]">{listing.priceData?.value?.formatted || "Price N/A"}</p>
                      <p className="text-sm text-foreground font-medium">{listing.address}</p>
                      <p className="text-muted-foreground text-sm flex items-center gap-3 mt-1">
                        {listing.bedrooms && (
                          <span className="flex items-center gap-1"><Bed className="w-3.5 h-3.5" /> {listing.bedrooms} Beds</span>
                        )}
                        {listing.fullBaths && (
                          <span className="flex items-center gap-1">
                            <Bath className="w-3.5 h-3.5" /> {listing.fullBaths}{partialBaths > 0 ? `.${partialBaths}` : ""} Baths
                          </span>
                        )}
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">{listing.cityName}, {listing.state}</p>
                      <Button onClick={scrollToForm} className="w-full mt-3 h-11 bg-[#C8102E] hover:bg-[#A00D24] text-white font-semibold">
                        Request a Showing
                      </Button>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="col-span-3 text-center py-8">
                <p className="text-muted-foreground">Listings are loading. Check back shortly or call us at <a href={PHONE_TEL} className="text-[#C8102E] font-semibold">{PHONE}</a>.</p>
              </div>
            )}
          </div>
          <div className="text-center">
            <a
              href={viewAllUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block"
            >
              <Button className="h-12 px-8 bg-[#C8102E] hover:bg-[#A00D24] text-white font-bold text-base">
                VIEW ALL AVAILABLE HOMES →
              </Button>
            </a>
            <p className="mt-3 text-sm text-muted-foreground flex items-center justify-center gap-1">
              <Flame className="w-4 h-4 text-orange-500" />
              {townName} homes are selling fast. Don't miss out.
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 4 — HOW IT WORKS */}
      <section className="py-16 bg-muted">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground text-center mb-10">How It Works — It Takes 60 Seconds</h2>
          <div className="grid md:grid-cols-3 gap-8 text-center">
            {[
              { icon: Home, title: "Tell Us Where You're Looking", desc: "Enter your target area and timeline." },
              { icon: Calendar, title: "Get Matched With Available Homes", desc: "We send you homes that fit your needs the same day." },
              { icon: KeyRound, title: "Schedule Your Showing", desc: "Book a showing in seconds. We handle the rest." },
            ].map((step, i) => (
              <div key={i} className="flex flex-col items-center">
                <div className="w-16 h-16 rounded-full bg-[#C8102E]/10 flex items-center justify-center mb-4">
                  <step.icon className="w-8 h-8 text-[#C8102E]" />
                </div>
                <div className="text-sm font-bold text-[#C8102E] mb-1">Step {i + 1}</div>
                <h3 className="font-bold text-foreground mb-2">{step.title}</h3>
                <p className="text-muted-foreground text-sm">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 5 — AGENT / TRUST */}
      <section className="py-16 bg-background">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground text-center mb-10">You'll Work With a Local {townName} Expert</h2>
          <div className="flex flex-col md:flex-row gap-8 mb-12">
            <div className="md:w-1/3 flex-shrink-0">
              {agentPhotoUrl ? (
                <img src={agentPhotoUrl} alt="Timothy Bowen — Prime Realty Partners" loading="lazy" className="w-full max-w-xs mx-auto rounded-lg shadow-lg object-cover" />
              ) : (
                <div className="w-full max-w-xs mx-auto rounded-lg shadow-lg bg-muted h-80 flex items-center justify-center text-muted-foreground">Loading...</div>
              )}
            </div>
            <div className="md:w-2/3">
              <p className="text-muted-foreground leading-relaxed mb-6">
                Our agents live and work in {townName}. We know every neighborhood — from school districts to market trends. We'll help you find the right home fast, negotiate the best price, and guide you through every step.
              </p>
              <div className="flex items-center gap-1 mb-6">
                {[1, 2, 3, 4, 5].map((s) => (
                  <Star key={s} className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                ))}
                <span className="text-sm text-muted-foreground ml-2">Google Reviews</span>
              </div>
              <div className="space-y-6">
                {TESTIMONIALS.map((t, i) => (
                  <blockquote key={i} className="border-l-4 border-[#C8102E] pl-4">
                    <div className="flex items-center gap-0.5 mb-1">
                      {[1, 2, 3, 4, 5].map((s) => (
                        <Star key={s} className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                      ))}
                    </div>
                    <p className="text-sm text-muted-foreground italic leading-relaxed">"{t.text}"</p>
                    <cite className="text-sm font-semibold text-foreground not-italic mt-1 block">— {t.name}, {t.town}</cite>
                  </blockquote>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 6 — SECOND CTA */}
      <section className="py-16 bg-[#C8102E]">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">Ready to See Homes This Week?</h2>
          <p className="text-white/90 mb-2">Spots for showings fill up fast. Claim your time now.</p>
          <p className="text-white/80 text-sm mb-6">🏡 Showings available as soon as today</p>
          <Button onClick={scrollToForm} className="h-12 px-10 bg-white text-[#C8102E] hover:bg-white/90 font-bold text-base">
            REQUEST A SHOWING →
          </Button>
          <div className="mt-4">
            <a href={PHONE_TEL} onClick={handlePhoneClick} className="text-white font-semibold hover:underline inline-flex items-center gap-2">
              <Phone className="w-4 h-4" /> {PHONE} — Call or Text Anytime
            </a>
          </div>
        </div>
      </section>

      {/* SECTION 7 — MINIMAL FOOTER */}
      <footer className="bg-foreground text-white py-10">
        <div className="max-w-5xl mx-auto px-4 text-center">
          <p className="text-lg font-bold mb-2">Prime Realty Partners</p>
          <p className="text-sm text-white/70 mb-1">31 Walnut Ave, Bellmawr, NJ 08031</p>
          <p className="text-sm text-white/70 mb-1">
            <a href={PHONE_TEL} onClick={handlePhoneClick} className="hover:underline">{PHONE}</a> · <a href="mailto:info@prpsj.com" className="hover:underline">info@prpsj.com</a>
          </p>
          <div className="flex items-center justify-center gap-4 mt-4 mb-4">
            <Link to="/privacy" className="text-xs text-white/50 hover:underline">Privacy Policy</Link>
            <Link to="/terms" className="text-xs text-white/50 hover:underline">Terms of Service</Link>
          </div>
          <div className="flex items-center justify-center gap-2 mb-3">
            <EqualHousingLogo size="sm" className="text-white/50" />
            <span className="text-xs text-white/50">Licensed Real Estate Broker · Member of Bright MLS</span>
          </div>
          <p className="text-[10px] text-white/40 max-w-2xl mx-auto">
            © 2026 Bright MLS. All rights reserved. Information Deemed Reliable, but Not Guaranteed. Listing information is from various brokers who participate in IDX and not all listings may be visible on the site. The property information being provided is for consumers personal, non-commercial use and may not be used for any purpose other than to identify prospective properties consumers may be interested in purchasing. Data services provided by IDX Broker.
          </p>
        </div>
      </footer>

      {/* MOBILE STICKY CTA */}
      {isMobile && formPastView && !submitted && (
        <>
          <div className="h-20 md:hidden" aria-hidden />
          <div className="fixed bottom-0 left-0 right-0 z-50 p-3 bg-white border-t shadow-2xl md:hidden">
            <Button onClick={scrollToForm} className="w-full h-12 bg-[#C8102E] hover:bg-[#A00D24] text-white font-bold text-base">
              See Available Homes →
            </Button>
          </div>
        </>
      )}
    </>
  );
};

export default TownBuyerLandingPage;
